import api from './api';

/**
 * Protected file access helper.
 *
 * Why this exists:
 * - Our API uses Bearer JWT auth (Cognito). Browsers do NOT automatically attach
 *   `Authorization` headers when you open a link in a new tab (`<a target="_blank">`).
 * - As a result, direct links to protected download endpoints return 401 even
 *   though normal XHR/fetch calls succeed.
 *
 * This helper fetches the file using our authenticated Axios instance and then:
 * - opens a blob URL in a new tab (preview), or
 * - triggers a browser download (download).
 */

function getFileNameFromPath(pathOrUrl) {
  try {
    const raw = String(pathOrUrl || '');
    const withoutQuery = raw.split('?')[0];
    const last = withoutQuery.split('/').filter(Boolean).pop();
    return last || null;
  } catch {
    return null;
  }
}

function getHeader(headers, key) {
  const k = String(key || '').toLowerCase();
  const h = headers || {};
  // Axios normalizes header keys to lowercase in browsers, but we guard anyway.
  return h[k] || h[key] || null;
}

function parseContentDispositionFilename(contentDisposition) {
  // Examples:
  // - attachment; filename="foo.pdf"
  // - attachment; filename*=UTF-8''foo%20bar.pdf
  const v = String(contentDisposition || '');
  const star = v.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].replace(/(^"|"$)/g, ''));
    } catch {
      return star[1].replace(/(^"|"$)/g, '');
    }
  }
  const simple = v.match(/filename\s*=\s*("?)([^";]+)\1/i);
  if (simple && simple[2]) return simple[2];
  return null;
}

async function fetchBlob(path) {
  const res = await api.get(path, { responseType: 'blob' });
  const contentType = getHeader(res.headers, 'content-type') || (res.data && res.data.type) || 'application/octet-stream';
  const contentDisposition = getHeader(res.headers, 'content-disposition');
  const fileNameFromHeader = parseContentDispositionFilename(contentDisposition);
  const fileNameFromPath = getFileNameFromPath(path);

  // Axios returns a Blob in browsers when responseType=blob.
  const blob = (res.data instanceof Blob && res.data.type)
    ? res.data
    : new Blob([res.data], { type: contentType });

  return {
    blob,
    contentType,
    fileName: fileNameFromHeader || fileNameFromPath || 'download',
  };
}

/**
 * Opens a protected file in a new tab by fetching it with auth first.
 * Returns true if a new tab was opened, false if we couldn't (in which case we trigger a download instead).
 */
export async function openProtectedPreview(path) {
  // Open the tab synchronously to avoid popup blockers.
  // Important: some browsers return `null` when using noopener/noreferrer, even though a tab opens.
  // We instead open without features, then explicitly null out opener to prevent tabnabbing.
  const tab = window.open('about:blank', '_blank');
  if (tab) {
    try { tab.opener = null; } catch {}
    try {
      tab.document.title = 'Loading preview…';
      tab.document.body.innerHTML = '<p style="font-family: system-ui; padding: 16px;">Loading preview…</p>';
    } catch {}
  }

  try {
    const { blob } = await fetchBlob(path);
    const objectUrl = URL.createObjectURL(blob);

    // Best case: we have a live tab reference.
    if (tab && !tab.closed) {
      tab.location.href = objectUrl;
    } else {
      // If we can't open a preview tab, do not navigate away from the SPA.
      // Fall back to downloading the file instead.
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = getFileNameFromPath(path) || 'preview';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    // Revoke later; keep long enough for the PDF/image to load reliably.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
    return !!(tab && !tab.closed);
  } catch (e) {
    // Avoid leaving a blank tab around if the authenticated fetch failed.
    try { if (tab && !tab.closed) tab.close(); } catch {}
    throw e;
  }
}

/**
 * Downloads a protected file by fetching it with auth first, then triggering a browser download.
 */
export async function downloadProtectedFile(path, opts = {}) {
  const { suggestedFileName } = opts;
  const { blob, fileName } = await fetchBlob(path);
  const objectUrl = URL.createObjectURL(blob);
  const finalName = suggestedFileName || fileName || 'download';

  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = finalName;
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60 * 1000);
}



